import { useEffect, useMemo, useState } from "react";
import Ethnicity from "../../models/serviceModels/Ethnicity";
import Gender from "../../models/serviceModels/Gender";
import Race from "../../models/serviceModels/Race";
import { getEthnicities, getGenders, getRaces } from "../../services/clientsService";
import ApiErrorMessages from "../UI/ApiErrorMessages";
import Loader from "../UI/Loader";
import Modal from "../UI/Modal";
import {
    DemographicsConfig,
    getDemographicsRaceMode,
    getEthnicityFieldVisible,
    getEthnicityPreviewOptions,
    getGenderOptionsForOrganization,
    getGenderPreviewOptions,
    getRaceFieldLabel,
    getRacePreviewOptions,
    isDemographicVisible
} from "../../utils/demographicsSchema";

interface DemographicsPreviewModalProps {
    visible: boolean;
    onClose: () => void;
    config: DemographicsConfig;
}

interface PreviewFieldProps {
    helperText: string;
    label: string;
    options?: string[];
    selectionType: 'text' | 'select' | 'radio' | 'checkbox';
}

const PreviewField = ({
    helperText,
    label,
    options = [],
    selectionType
}: PreviewFieldProps) => {
    const selectionMarker = selectionType === 'radio'
        ? '( )'
        : selectionType === 'checkbox'
            ? '[ ]'
            : '-';

    return (
        <div className="border rounded padding-3 margin-bottom-3">
            <label>{label}</label>
            <p className="margin-top-2 margin-bottom-0">{helperText}</p>
            {selectionType === 'text' && (
                <div className="border rounded padding-2 margin-top-3">
                    Example response
                </div>
            )}
            {selectionType !== 'text' && (
                <div className="margin-top-3">
                    {options.map(option => {
                        return (
                            <div key={`${label}-${option}`} className="margin-bottom-2">
                                {selectionMarker} {option}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const PreviewSection = ({
    config,
    description,
    ethnicityOptions,
    genderOptions,
    raceOptions,
    title
}: {
    config: DemographicsConfig;
    description: string;
    ethnicityOptions: string[];
    genderOptions: string[];
    raceOptions: string[];
    title: string;
}) => {
    const showPronouns = isDemographicVisible(config, 'pronouns');
    const showGender = isDemographicVisible(config, 'gender');
    const showEthnicity = getEthnicityFieldVisible(config);
    const showRace = isDemographicVisible(config, 'race');
    const raceFieldLabel = getRaceFieldLabel(config);
    const combinedRaceMode = getDemographicsRaceMode(config) === 'combined';

    return (
        <div className="column-medium-6">
            <div className="border rounded padding-4 margin-bottom-4">
                <h4>{title}</h4>
                <p>{description}</p>
                {showPronouns && (
                    <PreviewField
                        helperText="Optional text response"
                        label="Preferred Pronouns"
                        selectionType="text"
                    />
                )}
                {showGender && (
                    <PreviewField
                        helperText="Select one"
                        label="Gender"
                        options={genderOptions}
                        selectionType="select"
                    />
                )}
                {showEthnicity && (
                    <PreviewField
                        helperText="Select one"
                        label="Ethnicity"
                        options={ethnicityOptions}
                        selectionType="radio"
                    />
                )}
                {showRace && (
                    <PreviewField
                        helperText={combinedRaceMode ? 'Select all that apply' : 'Select all that apply'}
                        label={raceFieldLabel}
                        options={raceOptions}
                        selectionType="checkbox"
                    />
                )}
                {!showPronouns && !showGender && !showEthnicity && !showRace && (
                    <div className="border rounded padding-3">
                        All standard demographics fields are currently hidden in this preview.
                    </div>
                )}
            </div>
        </div>
    );
};

const DemographicsPreviewModal = ({ visible, onClose, config }: DemographicsPreviewModalProps) => {
    const [apiError, setApiError] = useState<any>();
    const [ethnicities, setEthnicities] = useState<Ethnicity[]>([]);
    const [genders, setGenders] = useState<Gender[]>([]);
    const [loading, setLoading] = useState(false);
    const [races, setRaces] = useState<Race[]>([]);

    useEffect(() => {
        if (!visible) {
            return;
        }

        setApiError(undefined);
        setLoading(true);

        Promise.all([getGenders(), getRaces(), getEthnicities()])
            .then(([loadedGenders, loadedRaces, loadedEthnicities]) => {
                setGenders(loadedGenders);
                setRaces(loadedRaces);
                setEthnicities(loadedEthnicities);
            })
            .catch(error => {
                console.error(error);
                setApiError(error);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [visible]);

    const genderOptions = useMemo(() => {
        const loadedOptions = getGenderOptionsForOrganization(genders, config).map(option => option.label);
        if (loadedOptions.length > 0) {
            return loadedOptions;
        }

        return getGenderPreviewOptions(config);
    }, [config, genders]);

    const ethnicityOptions = useMemo(() => {
        return getEthnicityPreviewOptions(ethnicities, config);
    }, [config, ethnicities]);

    const raceOptions = useMemo(() => {
        return getRacePreviewOptions(races, config);
    }, [config, races]);

    return (
        <Modal
            id="demographics-preview-modal"
            visible={visible}
            className="medium"
            title="Preview Demographic Fields"
            onClose={onClose}
        >
            <div className="margin-bottom-3 padding-3 border rounded">
                <strong>Preview note:</strong> These previews reflect your current unsaved selections.
            </div>
            <ApiErrorMessages error={apiError} />
            {loading && !apiError && <Loader />}
            {!loading && (
                <div className="row">
                    <PreviewSection
                        config={config}
                        description="This preview shows how standard demographics fields will appear when staff add or edit a participant."
                        ethnicityOptions={ethnicityOptions}
                        genderOptions={genderOptions}
                        raceOptions={raceOptions}
                        title="Participant Form Preview"
                    />
                    <PreviewSection
                        config={config}
                        description="This preview shows how those same standard demographics fields will appear on public intake forms after save."
                        ethnicityOptions={ethnicityOptions}
                        genderOptions={genderOptions}
                        raceOptions={raceOptions}
                        title="Public Intake Preview"
                    />
                </div>
            )}
        </Modal>
    );
};

export default DemographicsPreviewModal;
